import React, { useState } from 'react';
import styles from './OrderPage.module.css';
import { STRIPE_LINKS } from '../config/stripe';

const PLANS = [
  { id: 'basic', name: 'Basic', price: '$49/mo' },
  { id: 'standard', name: 'Standard', price: '$129/mo' },
  { id: 'pro', name: 'Pro', price: '$299/mo' },
  { id: 'agency', name: 'Agency', price: '$599/mo' }
];

const KARMA_PLANS = [
  { id: 'k-starter', name: 'Starter', price: '$29' },
  { id: 'k-trust', name: 'Trust', price: '$79' },
  { id: 'k-power', name: 'Power', price: '$179' },
  { id: 'k-authority', name: 'Authority', price: '$399' }
];

// Exporting defaults for Order component
export default function OrderPage() {
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  
  const [formData, setFormData] = useState({
    url: '',
    subreddits: '',
    instructions: '',
    email: '',
    name: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    setErrorMsg('');
    if (step === 1 && !selectedPlan) {
      setErrorMsg('Please select a plan to continue.');
      return;
    }
    if (step === 2) {
      if (!formData.url || !formData.subreddits || !formData.email || !formData.name) {
        setErrorMsg('Please fill in all required fields.');
        return;
      }
    }
    
    setStep(s => Math.min(s + 1, 3));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setErrorMsg('');
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setErrorMsg('');
  };

  return (
    <section className={styles.section}>
      <div className="container">
        
        <div className={styles.header}>
          <div className="section-label">Order setup</div>
          <h1 className="section-title">Start Your Campaign</h1>
        </div>

        {/* Progress Indicator */}
        <div className={styles.progressContainer}>
          <div className={`${styles.stepIndicator} ${step === 1 ? styles.active : ''} ${step > 1 ? styles.completed : ''}`}>
            <div className={styles.dot}>1</div>
            Select Plan
          </div>
          <div className={styles.line} />
          <div className={`${styles.stepIndicator} ${step === 2 ? styles.active : ''} ${step > 2 ? styles.completed : ''}`}>
            <div className={styles.dot}>2</div>
            Details
          </div>
          <div className={styles.line} />
          <div className={`${styles.stepIndicator} ${step === 3 ? styles.active : ''}`}>
            <div className={styles.dot}>3</div>
            Summary
          </div>
        </div>

        <div className={`${styles.panel} fade-up visible`}>
          {/* STEP 1 */}
          {step === 1 && (
            <div>
              <h2 className={styles.sectionTitle} style={{ marginTop: 0 }}>Monthly Subscriptions</h2>
              <div className={styles.grid}>
                {PLANS.map(plan => (
                  <div 
                    key={plan.id} 
                    className={`${styles.card} ${selectedPlan?.id === plan.id ? styles.selected : ''}`}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    <div className={styles.cardTitle}>{plan.name}</div>
                    <div className={styles.cardPrice}>{plan.price}</div>
                  </div>
                ))}
              </div>

              <h2 className={styles.sectionTitle}>Karma Packages (One-Time)</h2>
              <div className={styles.grid}>
                {KARMA_PLANS.map(plan => (
                  <div 
                    key={plan.id} 
                    className={`${styles.card} ${selectedPlan?.id === plan.id ? styles.selected : ''}`}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    <div className={styles.cardTitle}>{plan.name}</div>
                    <div className={styles.cardPrice}>{plan.price}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div>
              <h2 className={styles.sectionTitle} style={{ marginTop: 0 }}>Campaign Details</h2>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Reddit Post URL or Account URL *</label>
                <input 
                  type="text" 
                  name="url"
                  className={styles.input} 
                  placeholder="https://reddit.com/r/..."
                  value={formData.url}
                  onChange={handleInputChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Target Subreddits *</label>
                <input 
                  type="text" 
                  name="subreddits"
                  className={styles.input} 
                  placeholder="e.g. r/startups, r/marketing"
                  value={formData.subreddits}
                  onChange={handleInputChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Your Instructions / Special Notes (Optional)</label>
                <textarea 
                  name="instructions"
                  className={styles.textarea} 
                  placeholder="Any specific angles, tones, or competitor mentions?"
                  value={formData.instructions}
                  onChange={handleInputChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Your Name *</label>
                <input 
                  type="text" 
                  name="name"
                  className={styles.input} 
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Your Email Address *</label>
                <input 
                  type="email" 
                  name="email"
                  className={styles.input} 
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div>
              <h2 className={styles.sectionTitle} style={{ marginTop: 0 }}>Review Your Order</h2>
              
              <div className={styles.summaryBox}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Selected Plan</span>
                  <span className={styles.summaryValue}>{selectedPlan?.name} ({selectedPlan?.price})</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Target URL</span>
                  <span className={styles.summaryValue}>{formData.url}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Subreddits</span>
                  <span className={styles.summaryValue}>{formData.subreddits}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Name</span>
                  <span className={styles.summaryValue}>{formData.name}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Email</span>
                  <span className={styles.summaryValue}>{formData.email}</span>
                </div>
                {formData.instructions && (
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Instructions</span>
                    <span className={styles.summaryValue}>{formData.instructions}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {errorMsg && <div className={styles.errorText}>{errorMsg}</div>}

          {/* Nav Actions */}
          <div className={styles.actions}>
            {step > 1 ? (
              <button className="btn btn-outline" onClick={handleBack}>
                ← Back
              </button>
            ) : <div />}
            
            <div className={styles.actionsRight}>
              {step < 3 ? (
                <button className="btn btn-primary" onClick={handleNext}>
                  Next Step →
                </button>
              ) : (
                <button 
                  onClick={() => {
                    let key = '';
                    if (selectedPlan) {
                      if (selectedPlan.id.startsWith('k-')) {
                        key = `karma_${selectedPlan.name.toLowerCase()}`;
                      } else {
                        key = `${selectedPlan.id}_monthly`; 
                      }
                      const url = STRIPE_LINKS[key];
                      if (url) window.open(url, '_blank');
                    }
                  }}
                  className="btn btn-primary"
                >
                  Proceed to Payment →
                </button>
              )}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
